import { MasterRepository } from '~/repository/MasterRepository';
import { PublicRepository } from '~/repository/PublicRepository';
import { SystemRepository } from '~/repository/SystemRepository';
import type { UiMessage } from '~/types/api/master/getUiMessages';

const _cacheKey = {
  organization: 'organization',
  station: 'station',
  affiliationStation: 'affiliationStation',
  userCompany: 'userCompany',
  deliveryCompany: 'deliveryCompany',
  message: 'message',
  workType: 'workType',
  systemConfig: 'systemConfig',
  accountType: 'accountType',
  screenPatternName: 'screenPatternName',
} as const;
type CACHE_KEY = typeof _cacheKey[keyof typeof _cacheKey];

/**
 * マスタデータを管理するStore
 */
export const useMasterStore = defineStore('master', () => {
  const _cacheMap = new Map<CACHE_KEY, unknown>();
  const _promiseMap = new Map<CACHE_KEY, Promise<unknown>>();

  /**
   * 支社リストを取得する
   *
   * @param displayId 画面ID
   * @returns 支社リスト
   */
  const getOrganizations = async (displayId: string) => {
    const masterRepository = new MasterRepository(displayId);
    return await _getMasterWithCache(_cacheKey.organization, async () => {
      const response = await masterRepository.getOrganizations({});
      return response.data?.organization ?? [];
    }) ?? [];
  };

  /**
   * 駅リストを取得する
   *
   * @param displayId 画面ID
   * @returns 駅リスト
   */
  const getStations = async (displayId: string) => {
    const masterRepository = new MasterRepository(displayId);
    return await _getMasterWithCache(_cacheKey.station, async () => {
      const response = await masterRepository.getStations({});
      return response.data?.station ?? [];
    }) ?? [];
  };

  /**
   * 所属駅リストを取得する
   *
   * @param displayId 画面ID
   * @returns 所属駅リスト
   */
  const getAffiliationStations = async (displayId: string) => {
    const masterRepository = new MasterRepository(displayId);
    return await _getMasterWithCache(_cacheKey.affiliationStation, async () => {
      const response = await masterRepository.getAffiliationStations();
      return response.data?.station ?? [];
    }) ?? [];
  };

  /**
   * 利用者リストを取得する
   *
   * @param displayId 画面ID
   * @returns 利用者リスト
   */
  const getUserCompanies = async (displayId: string) => {
    const masterRepository = new MasterRepository(displayId);
    return await _getMasterWithCache(_cacheKey.userCompany, async () => {
      const response = await masterRepository.getUserCompanies({});
      return response.data?.userCompany ?? [];
    }) ?? [];
  };

  /**
   * 集配人リストを取得する
   *
   * @param displayId 画面ID
   * @returns 集配人リスト
   */
  const getDeliveryCompanies = async (displayId: string) => {
    const masterRepository = new MasterRepository(displayId);
    return await _getMasterWithCache(_cacheKey.deliveryCompany, async () => {
      const response = await masterRepository.getDeliveryCompanies({});
      return response.data?.deliveryCompany ?? [];
    }) ?? [];
  };

  /**
   * 作業種別リストを取得する
   *
   * @param displayId 画面ID
   * @returns 作業種別リスト
   */
  const getWorkTypes = async (displayId: string) => {
    const masterRepository = new MasterRepository(displayId);
    return await _getMasterWithCache(_cacheKey.workType, async () => {
      const response = await masterRepository.getWorkType();
      return response.data?.workType ?? [];
    }) ?? [];
  };

  /**
   * メッセージを取得する
   *
   * @param displayId 画面ID
   * @param messageCd メッセージコード
   */
  const getMessage = (messageCd: string) => {
    const messages = (_cacheMap.get(_cacheKey.message) as UiMessage[]) ?? [];
    return messages.find((m) => m.messageCd === messageCd);
  };

  /**
   * すべての画面メッセージを取得してメモリ上に保持する
   *
   * @param displayId 画面ID
   */
  const fetchAllMessages = async (displayId: string) => {
    const masterRepository = new MasterRepository(displayId);
    await _getMasterWithCache(_cacheKey.message, async () => {
      const response = await masterRepository.getUiMessages();
      return response.data?.message ?? [];
    });
  };

  /**
   * 指定の項目キーに紐づくシステム設定情報を取得する
   *
   * @param itemKey 項目キー
   * @returns 指定の項目キーに紐づくシステム設定情報
   */
  const getSystemConfig = async (displayId: string, itemKey: string) => {
    const systemRepository = new SystemRepository(displayId);
    const systemInfos = await _getMasterWithCache(_cacheKey.systemConfig, async () => {
      const response = await systemRepository.getSystemConfig({});
      return response.data?.systemInfo ?? [];
    }) ?? [];

    return systemInfos.filter((sc) => sc.itemKey === itemKey) ?? [];
  };

  /**
   * アカウント区分リストを取得する
   *
   * @param displayId 画面ID
   * @returns アカウント区分リスト
   */
  const getAccountTypes = async (displayId: string) => {
    const masterRepository = new MasterRepository(displayId);
    return await _getMasterWithCache(_cacheKey.accountType, async () => {
      const response = await masterRepository.getAccountTypes();
      return response.data?.accountGroups ?? [];
    }) ?? [];
  };

  /**
   * 画面パターン名を取得する
   *
   * @param displayId 画面ID
   * @returns 画面パターン名
   */
  const getScreenPatternName = async (displayId: string) => {
    const publicRepository = new PublicRepository(displayId);
    return await _getMasterWithCache(_cacheKey.screenPatternName, async () => {
      const response = await publicRepository.getScreenPatternName();
      return response.data?.screenPatternName ?? {};
    }) ?? {};
  };

  /**
   * キャッシュを利用してマスタデータを取得する
   *
   * @param key キャッシュキー（マスタごとに一意な文字列）
   * @param fetcher データ取得ロジック
   */
  const _getMasterWithCache = async <T>(key: CACHE_KEY, fetcher: () => Promise<T>): Promise<T> => {
    // キャッシュに存在する場合はそれを返却
    if (_cacheMap.get(key)) {
      return (_cacheMap.get(key)) as T;
    }

    // データ取得処理中の場合は待機して、処理結果を返却
    if (_promiseMap.get(key)) {
      return (await _promiseMap.get(key)) as T;
    }

    // キャッシュがなくデータ取得処理中でもない（初回アクセス）場合は、取得処理を実施
    const promise = fetcher().then((data) => {
      _cacheMap.set(key, data);
      return data;
    }).finally(() => {
      _promiseMap.delete(key);
    });

    _promiseMap.set(key, promise);

    return await promise;
  };

  /**
   * ストアの状態を初期化する
   */
  const reset = () => {
    _cacheMap.clear();
  };

  return {
    getOrganizations,
    getStations,
    getAffiliationStations,
    getUserCompanies,
    getDeliveryCompanies,
    getWorkTypes,
    getMessage,
    fetchAllMessages,
    getSystemConfig,
    getAccountTypes,
    getScreenPatternName,
    reset,
  };
});
